// services/companyProfileSync.js
// ─────────────────────────────────────────────────────────────────────────────
// Call syncCompanyProfile() immediately after any successful login.
// It fetches the company profile from the DB and writes it to AsyncStorage
// so every screen has the real company name, logo, VAT etc from first render.
// ─────────────────────────────────────────────────────────────────────────────
import AsyncStorage from '@react-native-async-storage/async-storage';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

/**
 * Fetch company profile from DB and save to AsyncStorage.
 * Called right after login — before navigating to WelcomeBusiness.
 *
 * @param {object} options
 * @param {string} [options.companyId]   - override company ID
 * @param {string} [options.token]       - auth token (reads from storage if not provided)
 * @returns {object|null}                - normalised profile or null on failure
 */
export const syncCompanyProfile = async (options = {}) => {
  try {
    const token = options.token
               || await AsyncStorage.getItem('auth_token')
               || await AsyncStorage.getItem('token');

    if (!token) {
      console.warn('[CompanySync] No auth token — skipping sync');
      return null;
    }

    // Resolve company ID from all possible sources
    let cid = options.companyId
            || await AsyncStorage.getItem('company_id')
            || await AsyncStorage.getItem('company_id');

    if (!cid) {
      try {
        const cp = await AsyncStorage.getItem('company_profile');
        if (cp) cid = JSON.parse(cp)?.id;
      } catch {}
    }

    if (!cid) {
      console.warn('[CompanySync] No company ID — skipping sync');
      return null;
    }

    console.log(`[CompanySync] Fetching company ${cid}...`);

    const res = await fetch(`${API_BASE_URL}/api/company/profile/${cid}`, {
      headers: {
        'X-API-Key':     API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type':  'application/json',
      },
    });

    if (!res.ok) {
      console.warn('[CompanySync] API error:', res.status);
      return null;
    }

    const data = await res.json();
    const raw  = data.data || data;

    if (!raw?.id) {
      console.warn('[CompanySync] No profile data returned');
      return null;
    }

    // Normalise all field name variants from DB → app profile shape
    const profile = {
      id:                 String(raw.id),
      name:               raw.company_name  || raw.name          || '',
      email:              raw.email         || raw.company_email  || '',
      phone:              raw.phone         || raw.company_phone  || '',
      website:            raw.website       || '',
      industry:           raw.industry      || '',
      companySize:        raw.employee_count ? String(raw.employee_count) : '',
      logo:               raw.logo          || raw.logo_url
                        || raw.company_logo || raw.banner_image_url || null,
      headquarters:       raw.address       || raw.headquarters   || '',
      description:        raw.description   || '',
      vatNumber:          raw.vat_number    || raw.vatNumber      || '',
      registrationNumber: raw.registration_number || raw.registrationNumber || '',
      companyCode:        raw.company_code  || raw.companyCode    || '',
      companyType:        raw.company_type  || '',
      founded:            raw.founded       ? String(raw.founded) : '',
      linkedIn:           raw.linkedin_url  || raw.linkedIn       || '',
      twitterUrl:         raw.twitter_url   || '',
      facebookUrl:        raw.facebook_url  || '',
      // Invoice settings
      paymentTerms:       String(raw.payment_terms    || 30),
      defaultTaxRate:     String(raw.default_tax_rate || 20),
      invoicePrefix:      raw.invoice_prefix || 'INV',
    };

    // Write to all AsyncStorage keys used across the app
    await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
    await AsyncStorage.setItem('company_id',      String(raw.id));
    await AsyncStorage.setItem('company_id',     String(raw.id));

    console.log(`[CompanySync] ✅ Company synced: ${profile.name} (id:${profile.id})`);
    return profile;

  } catch (e) {
    console.warn('[CompanySync] Error:', e.message);
    return null;
  }
};

/**
 * Sync subscription too — call after login for full account state.
 */
export const syncSubscription = async () => {
  try {
    const token = await AsyncStorage.getItem('auth_token')
               || await AsyncStorage.getItem('token');
    const cid   = await AsyncStorage.getItem('company_id')
               || await AsyncStorage.getItem('company_id');

    if (!token || !cid) return null;

    const res = await fetch(
      `${API_BASE_URL}/api/business-subscriptions?company_id=${cid}&status=active`,
      { headers: { 'X-API-Key': API_KEY, 'Authorization': `Bearer ${token}` } }
    );

    if (!res.ok) return null;
    const data = await res.json();
    const sub  = Array.isArray(data.data) ? data.data[0] : data.data;
    if (!sub) return null;

    const normalised = {
      id:            sub.id,
      plan_id:       sub.plan_id       || 'single_starter',
      plan_name:     sub.plan_name     || sub.name || sub.plan_id || 'Business Plan',
      plan_type:     sub.plan_type     || 'single_user',
      price:         parseFloat(sub.price || sub.price_paid || 0),
      billing_cycle: sub.billing_cycle || 'monthly',
      max_users:     sub.max_users     || sub.users_count || 1,
      features:      Array.isArray(sub.features) ? sub.features
                   : Array.isArray(sub.plan_features) ? sub.plan_features : [],
      status:        sub.status        || 'active',
      start_date:    sub.start_date,
      end_date:      sub.end_date,
      company_id:   cid,
    };

    await AsyncStorage.setItem('business_subscription', JSON.stringify(normalised));
    console.log(`[CompanySync] ✅ Subscription synced: ${normalised.plan_name} £${normalised.price}/mo`);
    return normalised;

  } catch (e) {
    console.warn('[CompanySync] Subscription sync error:', e.message);
    return null;
  }
};

/**
 * Master sync — call this single function right after login success.
 * Runs company + subscription sync in parallel.
 */
export const syncAfterLogin = async (options = {}) => {
  const [profile, subscription] = await Promise.all([
    syncCompanyProfile(options),
    syncSubscription(),
  ]);
  return { profile, subscription };
};

export default { syncAfterLogin, syncCompanyProfile, syncSubscription };
